<template>
  <div class="phone-task-detail" data-testid="phone-task-detail">
    <router-link
      to="/your-stuff/tasks"
      class="phone-task-detail__back"
      data-testid="phone-task-back"
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M15 18l-6-6 6-6" />
      </svg>
      Your Tasks
    </router-link>

    <PhoneTaskState
      v-if="viewState === 'loading'"
      title="Loading task"
      data-testid="phone-task-loading"
    />
    <PhoneTaskState
      v-else-if="viewState === 'offline'"
      title="You're offline"
      text="This task can't be shown without a connection. Reconnect to see it."
      action-label="Try again"
      data-testid="phone-task-offline"
      @action="load"
    />
    <PhoneTaskState
      v-else-if="viewState === 'unavailable'"
      title="Task not available"
      text="This task no longer exists or is no longer assigned to you."
      action-label="Back to Your Tasks"
      action-to="/your-stuff/tasks"
      data-testid="phone-task-unavailable"
    />
    <PhoneTaskState
      v-else-if="viewState === 'error'"
      title="Couldn't load this task"
      text="Something went wrong while loading the task."
      action-label="Try again"
      data-testid="phone-task-error"
      @action="load"
    />

    <article v-else-if="task" class="phone-task-detail__card" data-testid="phone-task-card">
      <header class="phone-task-detail__header">
        <PhoneTaskTypeIcon :kind="task.kind" />
        <h1 class="phone-task-detail__title" data-testid="phone-task-type">{{ task.typeLabel }}</h1>
      </header>

      <dl class="phone-task-detail__fields">
        <div class="phone-task-detail__field">
          <dt class="phone-task-detail__label">Status</dt>
          <dd class="phone-task-detail__value">
            <TaskStatusBadge :task-status="task.status" />
          </dd>
        </div>

        <template v-if="task.hasCheckpoint">
          <div v-if="task.checkpoint" class="phone-task-detail__field">
            <dt class="phone-task-detail__label">Checkpoint</dt>
            <dd class="phone-task-detail__value" data-testid="phone-task-checkpoint">
              {{ task.checkpoint }}
            </dd>
          </div>
          <div v-if="task.responsibility" class="phone-task-detail__field">
            <dt class="phone-task-detail__label">Responsibility</dt>
            <dd class="phone-task-detail__value" data-testid="phone-task-responsibility">
              {{ task.responsibility }}
            </dd>
          </div>
        </template>
        <div v-else-if="task.summary" class="phone-task-detail__field">
          <dt class="phone-task-detail__label">Description</dt>
          <dd class="phone-task-detail__value" data-testid="phone-task-description">
            {{ task.summary }}
          </dd>
        </div>

        <div
          v-if="deadlineLabel"
          class="phone-task-detail__field"
          data-testid="phone-task-deadline"
        >
          <dt class="phone-task-detail__label">Deadline</dt>
          <dd class="phone-task-detail__value">{{ deadlineLabel }}</dd>
        </div>
      </dl>

      <p
        v-if="terminalSummary"
        class="phone-task-detail__summary"
        data-testid="phone-task-terminal"
      >
        {{ terminalSummary }}
      </p>
    </article>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import TaskStatusBadge from "@components/TaskStatusBadge/TaskStatusBadge.vue";
import { getAccountTask } from "@/api/tasksAPI";
import PhoneTaskState from "../../components/PhoneTaskState/PhoneTaskState.vue";
import PhoneTaskTypeIcon from "../../components/PhoneTaskTypeIcon/PhoneTaskTypeIcon.vue";
import { useOnlineStatus } from "@/composables/useOnlineStatus";
import { formatPhoneTaskDate, getCurrentAccountId, mapPhoneTask } from "@/utils/phoneTasks";

const props = defineProps({
  taskId: {
    type: [String, Number],
    required: true,
  },
});

const { isOnline } = useOnlineStatus();

const viewState = ref("loading");
const task = ref(null);

const deadlineLabel = computed(() => formatPhoneTaskDate(task.value?.deadline));

const terminalSummary = computed(() => {
  if (!task.value?.isTerminal) return "";
  const verb = task.value.status === "canceled" ? "Canceled" : "Completed";
  const when = formatPhoneTaskDate(task.value.doneOn);
  return when ? `${verb} on ${when}` : verb;
});

const load = async () => {
  if (!isOnline.value) {
    task.value = null;
    viewState.value = "offline";
    return;
  }
  viewState.value = "loading";
  try {
    const { data } = await getAccountTask(getCurrentAccountId(), props.taskId);
    task.value = mapPhoneTask(data);
    viewState.value = "ready";
  } catch (error) {
    task.value = null;
    if (error?.response?.status === 404) {
      viewState.value = "unavailable";
      return;
    }
    console.error("Failed to load phone task", error);
    viewState.value = "error";
  }
};

watch(isOnline, (online, wasOnline) => {
  if (online && !wasOnline) load();
  if (!online && viewState.value !== "ready") viewState.value = "offline";
});

watch(() => props.taskId, load);

onMounted(load);
</script>

<style scoped>
.phone-task-detail {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  min-height: 100%;
  padding: 16px;
  background: #f9fafb;
  font-family: "Inter", sans-serif;
}

.phone-task-detail__back {
  display: inline-flex;
  align-items: center;
  align-self: flex-start;
  gap: 4px;
  min-height: 44px;
  padding: 0 8px 0 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #475467;
  text-decoration: none;
}

.phone-task-detail__back:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.phone-task-detail__card {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
}

.phone-task-detail__header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.phone-task-detail__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
}

.phone-task-detail__fields {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0;
}

.phone-task-detail__field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.phone-task-detail__label {
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #475467;
}

.phone-task-detail__value {
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #101828;
  overflow-wrap: anywhere;
}

.phone-task-detail__summary {
  margin: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}
</style>
